import React, { useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import { getAuthItem } from '../../utils/auth'
import { useBarista } from '../../contexts/BaristaContext'
import { offlineDB } from '../../offline/db'
import { useNetworkStatus } from '../../offline/useNetworkStatus'
import { enqueueAction } from '../../offline/syncQueue'
import {
    cacheOrderItemsForOrders,
    getBranchStaffContext,
    getActiveRemakeRequestsOffline,
    submitRemakeRequestOffline,
} from '../../offline/posOfflineHelpers'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

export default function OrderManagement({ onNewOrder }) {
    const [loading, setLoading] = useState(true)
    const isOnline = useNetworkStatus()
    const [staffId, setStaffId] = useState(null)
    const [branchId, setBranchId] = useState(null)
    const [branchName, setBranchName] = useState('')
    const [staffRole, setStaffRole] = useState('')
    const { selectedBarista } = useBarista()
    const canUpdateOrders = staffRole === 'Barista'

    const [orders, setOrders] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [filterStatus, setFilterStatus] = useState('All')
    const [dateFilter, setDateFilter] = useState('all')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd]     = useState('')

    useEffect(() => {
        const applyFilterHandoff = () => {
            try {
                const raw = sessionStorage.getItem('staffDashboardFilterHandoff')
                if (raw) {
                    const handoff = JSON.parse(raw)
                    if (handoff.preset === '1d') {
                        // Dashboard's "Today" preset maps to Order Management's
                        // own "Today" filter (calendar day), no custom range needed.
                        setDateFilter('day')
                        setCustomStart('')
                        setCustomEnd('')
                    } else if (handoff.customStart && handoff.customEnd) {
                        setDateFilter('custom')
                        setCustomStart(handoff.customStart)
                        setCustomEnd(handoff.customEnd)
                    }
                    sessionStorage.removeItem('staffDashboardFilterHandoff')
                }
            } catch (err) {
                console.error('Failed to apply staff dashboard filter handoff:', err)
            }
        }
        applyFilterHandoff()

        window.addEventListener('kaperk:orders-filter-handoff', applyFilterHandoff)
        return () => window.removeEventListener('kaperk:orders-filter-handoff', applyFilterHandoff)
    }, [])


    const [showViewModal, setShowViewModal] = useState(false)
    const [showVoidedItems, setShowVoidedItems] = useState(false)
    const [showCancelPinModal, setShowCancelPinModal] = useState(false)
    const [cancelPinInput, setCancelPinInput] = useState('')
    const [pendingCancelOrderId, setPendingCancelOrderId] = useState(null)

    const [showRemakeModal, setShowRemakeModal] = useState(false)
    const [remakeOrder, setRemakeOrder] = useState(null)
    const [remakeOrderItems, setRemakeOrderItems] = useState([])
    const [remakeReason, setRemakeReason] = useState('')
    const [remakeOtherReason, setRemakeOtherReason] = useState('')
    const [remakeResponsibleStaff, setRemakeResponsibleStaff] = useState(new Set())
    const [selectedRemakeItemIds, setSelectedRemakeItemIds] = useState(new Set())
    const [submittingRemake, setSubmittingRemake] = useState(false)
    const [remakeRequestOrderIds, setRemakeRequestOrderIds] = useState(new Set())

    const REMAKE_REASONS = [
        'Wrong item prepared', 'Wrong size', 'Wrong modifier', 'Missing item',
        'Incorrect quantity', 'Customer requested correction', 'Incorrect customization', 'Other',
    ]

    const remakeStaffOptions = useMemo(() => {
        if (!remakeOrder) return []
        const options = []
        if (remakeOrder.pos_barista_info) {
            options.push({
                key: `pos_${remakeOrder.pos_barista_info.barista_id}`,
                staffId: remakeOrder.pos_barista_info.barista_id,
                role: 'POS',
                name: `${remakeOrder.pos_barista_info.first_name} ${remakeOrder.pos_barista_info.last_name}`,
            })
        }
        if (remakeOrder.barista_info && remakeOrder.barista_info.barista_id !== remakeOrder.pos_barista_info?.barista_id) {
            options.push({
                key: `om_${remakeOrder.barista_info.barista_id}`,
                staffId: remakeOrder.barista_info.barista_id,
                role: 'Order Management',
                name: `${remakeOrder.barista_info.first_name} ${remakeOrder.barista_info.last_name}`,
            })
        }
        return options
    }, [remakeOrder])

    const fetchActiveRemakeRequestOrderIds = async () => {
        if (!branchId) return
        if (!isOnline) {
            const cached = await getActiveRemakeRequestsOffline(branchId)
            setRemakeRequestOrderIds(new Set(cached.map(r => r.order_id)))
            return
        }
        try {
            const { data, error } = await supabase
                .from('remake_requests')
                .select('order_id')
                .eq('branch_id', branchId)
                .in('status', ['pending', 'in_progress'])
            if (error) throw error
            setRemakeRequestOrderIds(new Set((data || []).map(r => r.order_id)))
        } catch (err) {
            console.error('Failed to fetch active remake requests:', err)
        }
    }

    useEffect(() => {
        if (!branchId) return
        fetchActiveRemakeRequestOrderIds()
        if (!isOnline) return
        const channel = supabase
            .channel(`remake-requests-status-${branchId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'remake_requests', filter: `branch_id=eq.${branchId}` },
                () => fetchActiveRemakeRequestOrderIds())
            .subscribe()
        return () => supabase.removeChannel(channel)
    }, [branchId, isOnline])

    const handleOpenRemakeModal = async (order) => {
        setRemakeOrder(order)
        setRemakeReason('')
        setRemakeOtherReason('')
        setRemakeResponsibleStaff(new Set())
        setSelectedRemakeItemIds(new Set())
        setShowRemakeModal(true)

        const loadFromCache = async () => {
            const cached = await offlineDB.orderItemsCache.where('order_id').equals(order.order_id).toArray()
            return cached.filter(i => !['voided', 'void_approved'].includes(i.line_status))
        }

        try {
            let data
            if (!isOnline || String(order.order_id).startsWith('local_')) {
                data = await loadFromCache()
            } else {
                const { data: rows, error } = await supabase
                    .from('order_items')
                    .select(`*, menu_items ( name ), order_item_variants ( item_variants ( variant_name ) ), order_item_modifiers ( item_modifiers ( modifier_name ) )`)
                    .eq('order_id', order.order_id)
                    .not('line_status', 'in', '("voided","void_approved")')
                if (error) throw error
                data = rows
            }
            setRemakeOrderItems(data || [])
            setSelectedRemakeItemIds(new Set((data || []).map(i => i.order_item_id)))
        } catch (err) {
            console.error('Failed to load order items for remake:', err)
            const data = await loadFromCache().catch(() => [])
            setRemakeOrderItems(data)
            setSelectedRemakeItemIds(new Set(data.map(i => i.order_item_id)))
        }
    }

    const handleToggleRemakeItem = (orderItemId) => {
        setSelectedRemakeItemIds(prev => {
            const next = new Set(prev)
            next.has(orderItemId) ? next.delete(orderItemId) : next.add(orderItemId)
            return next
        })
    }

    const handleToggleRemakeStaff = (key) => {
        setRemakeResponsibleStaff(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }

    const handleSubmitRemakeRequest = async () => {
        if (!remakeOrder) return

        const finalReason = remakeReason === 'Other' ? remakeOtherReason.trim() : remakeReason
        if (!finalReason) {
            Swal.fire({ icon: 'warning', title: 'Reason Required', text: 'Please select or enter a reason for the remake.', confirmButtonColor: '#A0826D' })
            return
        }
        if (remakeResponsibleStaff.size === 0) {
            Swal.fire({ icon: 'warning', title: 'Responsible Staff Required', text: 'Please select at least one responsible staff member.', confirmButtonColor: '#A0826D' })
            return
        }
        if (selectedRemakeItemIds.size === 0) {
            Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one item to remake.', confirmButtonColor: '#A0826D' })
            return
        }

        const responsiblePayload = remakeStaffOptions
            .filter(opt => remakeResponsibleStaff.has(opt.key))
            .map(opt => ({ staff_id: opt.staffId, role: opt.role, name: opt.name }))

        const remakeItemsPayload = remakeOrderItems
            .filter(it => selectedRemakeItemIds.has(it.order_item_id))
            .map(it => ({
                order_item_id: it.order_item_id,
                item_id: it.item_id,
                name: it.menu_items?.name || it.name || 'Item',
                quantity: it.quantity,
            }))

        // Offline, OR the order itself hasn't synced yet → queue it
        if (!isOnline || String(remakeOrder.order_id).startsWith('local_')) {
            setSubmittingRemake(true)
            try {
                await submitRemakeRequestOffline({
                    branchId,
                    orderId: remakeOrder.order_id,
                    referenceNumber: remakeOrder.reference_number,
                    reason: finalReason,
                    responsibleStaff: responsiblePayload,
                    remakeItems: remakeItemsPayload,
                    submittedBy: staffId,
                    submittedByBarista: selectedBarista
                        ? { barista_id: selectedBarista.barista_id, first_name: selectedBarista.first_name, last_name: selectedBarista.last_name }
                        : null,
                })
                setShowRemakeModal(false); setRemakeOrder(null); setRemakeOrderItems([])
                fetchActiveRemakeRequestOrderIds()
                Swal.fire({ icon: 'info', title: 'Remake Queued', text: 'It will reach POS once wifi returns.', confirmButtonColor: '#A0826D', timer: 2200 })
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Failed to Queue', text: err.message, confirmButtonColor: '#A0826D' })
            } finally {
                setSubmittingRemake(false)
            }
            return
        }

        setSubmittingRemake(true)
        try {
            const responsiblePayload = remakeStaffOptions
                .filter(opt => remakeResponsibleStaff.has(opt.key))
                .map(opt => ({ staff_id: opt.staffId, role: opt.role, name: opt.name }))

            const remakeItemsPayload = remakeOrderItems
                .filter(it => selectedRemakeItemIds.has(it.order_item_id))
                .map(it => ({
                    order_item_id: it.order_item_id,
                    item_id: it.item_id,
                    name: it.menu_items?.name || 'Item',
                    quantity: it.quantity,
                }))

            const { data: remakeRequest, error } = await supabase
                .from('remake_requests')
                .insert([{
                    order_id: remakeOrder.order_id,
                    reference_number: remakeOrder.reference_number,
                    branch_id: branchId,
                    reason: finalReason,
                    responsible_staff: responsiblePayload,
                    remake_items: remakeItemsPayload,
                    submitted_by: staffId,
                    submitted_by_barista: selectedBarista
                        ? { barista_id: selectedBarista.barista_id, first_name: selectedBarista.first_name, last_name: selectedBarista.last_name }
                        : null,
                    status: 'pending',
                }])
                .select()
                .single()
            if (error) throw error

            const activityRows = responsiblePayload.map(p => ({
                barista_id: p.staff_id,
                branch_id: branchId,
                activity_type: 'remake_request',
                order_id: remakeOrder.order_id,
                reference_number: remakeOrder.reference_number,
                reason: finalReason,
                remake_request_id: remakeRequest.remake_request_id,
            }))
            if (activityRows.length > 0) {
                const { error: activityError } = await supabase.from('shift_activity_log').insert(activityRows)
                if (activityError) console.error('Failed to log shift activity:', activityError)
            }

            setShowRemakeModal(false)
            setRemakeOrder(null)
            setRemakeOrderItems([])
            fetchActiveRemakeRequestOrderIds()
            Swal.fire({ icon: 'success', title: 'Remake Requested', text: 'The remake request has been sent to POS.', confirmButtonColor: '#A0826D', timer: 2000 })
        } catch (err) {
            console.error('Error submitting remake request:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Submit', text: err.message || 'An error occurred.', confirmButtonColor: '#A0826D' })
        } finally {
            setSubmittingRemake(false)
        }
    }

    const [viewingOrder, setViewingOrder] = useState(null)
    const [orderItems, setOrderItems] = useState([])
    const [deliveryInfo, setDeliveryInfo] = useState(null)
    const [deliveryInfoLoading, setDeliveryInfoLoading] = useState(false)

    const [pendingPage, setPendingPage] = useState(1)
    const [preparingPage, setPreparingPage] = useState(1)
    const [readyPage, setReadyPage] = useState(1)
    const [completedPage, setCompletedPage] = useState(1)
    const [cancelledPage, setCancelledPage] = useState(1)
    const [filteredPage, setFilteredPage] = useState(1)

    const [pendingSyncOrders, setPendingSyncOrders] = useState([])
    const PAGE_SIZE = 12

    useEffect(() => {
        initializeBranchOrders()
    }, [])

    useEffect(() => {
        fetchPendingSyncOrders()
    }, [])

    const isOnlineRef = useRef(isOnline)
    useEffect(() => { isOnlineRef.current = isOnline }, [isOnline])

    useEffect(() => {
        if (!branchId) return
        const handleSyncComplete = () => {
            fetchOrders(branchId)
            fetchPendingSyncOrders()
        }
        const handleOrderCreated = () => {
            console.log('[OrderManagement] received event, branchId:', branchId)   // ← is this actually here?
            fetchOrders(branchId)
        }
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        window.addEventListener('kaperk:order-created', handleOrderCreated)
        return () => {
            window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
            window.removeEventListener('kaperk:order-created', handleOrderCreated)
        }
    }, [branchId])

    useEffect(() => {
        if (!branchId) return

        const channel = supabase
            .channel(`branch-orders-${branchId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'orders', filter: `branch_id=eq.${branchId}` },
                (payload) => {
                    fetchOrders(branchId)
                    if (onNewOrder) onNewOrder(payload.new)
                    triggerBrowserNotif(payload.new)
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'orders', filter: `branch_id=eq.${branchId}` },
                () => fetchOrders(branchId)
            )

        // Only listen to order_items for the order currently open in the modal —
        // scoping by order_id keeps the filter tiny and avoids resubscribing
        // every time the branch's order list changes.
        if (viewingOrder?.order_id) {
            channel.on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'order_items', filter: `order_id=eq.${viewingOrder.order_id}` },
                () => handleViewOrder(viewingOrder)
            )
        }

        channel.subscribe()

        return () => supabase.removeChannel(channel)
    }, [branchId, viewingOrder?.order_id])

    useEffect(() => {
        const handler = () => fetchActiveRemakeRequestOrderIds()
        window.addEventListener('kaperk:remake-request-changed', handler)
        return () => window.removeEventListener('kaperk:remake-request-changed', handler)
    }, [branchId])
        
    // Icons
    const PendingIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 34 34"
            fill="none"
            className="w-5 h-5"
        >
            <path
            d="M1.5 16.8947C1.5 18.9163 1.8982 20.9182 2.67185 22.786C3.44551 24.6537 4.57947 26.3508 6.009 27.7804C7.43853 29.2099 9.13562 30.3439 11.0034 31.1175C12.8712 31.8912 14.873 32.2894 16.8947 32.2894C18.9163 32.2894 20.9182 31.8912 22.786 31.1175C24.6537 30.3439 26.3508 29.2099 27.7804 27.7804C29.2099 26.3508 30.3439 24.6537 31.1175 22.786C31.8912 20.9182 32.2894 18.9163 32.2894 16.8947C32.2894 12.8118 30.6674 8.89606 27.7804 6.009C24.8933 3.12194 20.9776 1.5 16.8947 1.5C12.8118 1.5 8.89606 3.12194 6.009 6.009C3.12193 8.89606 1.5 12.8118 1.5 16.8947Z"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            />
            <path
            d="M16.8945 8.3418V16.8944L22.0261 22.026"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            />
        </svg>
    )

    const PreparingIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 91 91"
            fill="currentColor"
            className="w-5 h-5"
        >
            <path d="M45.5 17.7734C44.0781 17.7734 42.6562 18.4844 39.8125 19.9062L41.7406 25.6903C31.9326 26.1671 20.0413 28.4446 17.5388 32.5254H73.4585C72.2122 30.4953 68.6431 28.9113 64.1621 27.7731V23.9941H60.9629V27.0629C57.1944 26.3329 53.0715 25.8756 49.2594 25.6903L51.1875 19.9062C48.3438 18.4844 46.9219 17.7734 45.5 17.7734ZM17.0625 35.7246V38.2129H9.77539V41.4121H17.0625V68.25C17.0625 71.0938 19.9062 73.9375 22.75 73.9375H68.25C71.0938 73.9375 73.9375 71.0938 73.9375 68.25V41.4121H81.2246V38.2129H73.9375V35.7246H17.0625Z"/>
        </svg>
    )

    // Initialize
    // const initializeBranchOrders = async () => {
    //     try {
    //         setLoading(true)

    //         const sessionStaffId = getAuthItem('staffId')
    //         if (!sessionStaffId) {
    //             throw new Error('Staff session not found.')
    //         }

    //         const { data: accountData, error: accountError } = await supabase
    //             .from('staff_accounts')
    //             .select('id, staff_id')
    //             .or(`id.eq.${sessionStaffId},staff_id.eq.${sessionStaffId}`)
    //             .single()

    //         if (accountError) throw accountError
    //         if (!accountData?.staff_id) {
    //             throw new Error('No linked staff record found for this account.')
    //         }

    //         const { data: staffData, error: staffError } = await supabase
    //             .from('staff')
    //             .select(`
    //                 id,
    //                 role,
    //                 branch_id,
    //                 branches (
    //                     branch_id,
    //                     name
    //                 )
    //             `)
    //             .eq('id', accountData.staff_id)
    //             .single()

    //         if (staffError) throw staffError
    //         if (!staffData?.branch_id) {
    //             throw new Error('No branch assigned to this staff account.')
    //         }

    //         setStaffId(accountData.staff_id) 
    //         setBranchId(staffData.branch_id)
    //         setBranchName(staffData.branches?.name || 'Assigned Branch')
    //         setStaffRole(staffData.role || '')

    //         await fetchOrders(staffData.branch_id)
    //     } catch (err) {
    //         console.error('Error initializing branch orders:', err)
    //         Swal.fire({
    //             icon: 'error',
    //             title: 'Failed to Load Orders',
    //             text: err.message || 'An error occurred while loading branch orders.',
    //             confirmButtonColor: '#A0826D'
    //         })
    //     } finally {
    //         setLoading(false)
    //     }
    // }

    const initializeBranchOrders = async () => {
        try {
            setLoading(true)
            const staffData = await getBranchStaffContext(isOnline)

            setStaffId(staffData.id)
            setBranchId(staffData.branch_id)
            setBranchName(staffData.branch_name)
            setStaffRole(staffData.role)

            await fetchOrders(staffData.branch_id)
        } catch (err) {
            console.error('Error initializing branch orders:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Orders',
                text: err.message || 'An error occurred while loading branch orders.',
                confirmButtonColor: '#A0826D'
            })
        } finally {
            setLoading(false)
        }
    }

    

    const triggerBrowserNotif = (order) => {
        if (!('Notification' in window)) return
        if (Notification.permission === 'granted') {
            new Notification('New Order Received!', {
                body: `Order #${order.reference_number} — ${
                    order.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in'
                } · ₱${parseFloat(order.final_amount || 0).toFixed(2)}`,
            })
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(perm => {
                if (perm === 'granted') triggerBrowserNotif(order)
            })
        }
    }

    const fetchOrders = async (currentBranchId) => {
        try {
           if (!isOnlineRef.current) {
                const cached = await offlineDB.ordersCache.toArray()
                console.log('[fetchOrders] all cached orders:', cached.map(o => ({ id: o.order_id, branch: o.branch_id })))
                console.log('[fetchOrders] filtering for branchId:', currentBranchId)
                const filtered = cached
                    .filter(o => o.branch_id === currentBranchId)
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                setOrders(filtered)
                return
            }

            const { data, error } = await supabase
                .from('orders')
                .select(`
                    order_id,
                    user_id,
                    client_ref,
                    reference_number,
                    branch_id,
                    order_source,
                    fulfillment_type,
                    order_status,
                    subtotal,
                    discount,
                    tax,
                    final_amount,
                    created_at,
                    updated_at,
                    completed_at,
                    barista_id,
                    order_completed_by,
                    customer_notes,
                    address_id,
                    with_cutlery,
                    is_remake,
                    original_order_id,
                    barista_info:barista_info!orders_order_completed_by_fkey ( barista_id, first_name, last_name ),
                    pos_barista_info:barista_info!orders_pos_processed_by_fkey ( barista_id, first_name, last_name ),
                    delivery_address:customer_addresses!orders_address_id_fkey ( label, full_address ),
                    customer:customers!orders_user_id_fkey ( first_name, last_name, phone_number, email ),
                    order_transactions ( status, payment_method )
                `)
                .eq('branch_id', currentBranchId)
                .order('created_at', { ascending: false })

            console.log('[DEBUG] orders with barista_info:', data?.filter(o => o.order_completed_by).map(o => ({
                order_id: o.order_id,
                order_completed_by: o.order_completed_by,
                barista_info: o.barista_info
            })))

            if (error) throw error

            setOrders(data || [])

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kaperk:orders-changed'))
            }

            await offlineDB.ordersCache.clear()
            await offlineDB.ordersCache.bulkPut(data || [])
            cacheOrderItemsForOrders((data || []).map(o => o.order_id))
                .catch(err => console.error('Order items pre-cache failed:', err))
        } catch (err) {
            console.error('Error fetching branch orders:', err)
            // If online fetch fails (e.g. flaky connection), fall back to cache
            // instead of showing an empty screen.
            const cached = await offlineDB.ordersCache.toArray()
            const filtered = cached.filter(o => o.branch_id === currentBranchId)
            if (filtered.length > 0) {
                setOrders(filtered)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Fetch Orders',
                    text: err.message || 'Unable to load branch orders.',
                    confirmButtonColor: '#A0826D'
                })
                setOrders([])
            }
        }
    }

    const fetchPendingSyncOrders = async () => {
        const queued = await offlineDB.syncQueue
            .where('type').equals('create_order')
            .and(a => a.status === 'pending' || a.status === 'error')
            .toArray()
        setPendingSyncOrders(queued)
    }

    const handleViewOrder = async (order) => {
        setViewingOrder(order)
        console.log('order_source:', order.order_source, 'customer:', order.customer, 'address:', order.delivery_address)

        setDeliveryInfo(null)

        if (order.fulfillment_type === 'delivery') {
            setDeliveryInfoLoading(true)

            let customerData = order.customer || null
            let addressData = order.delivery_address || null

            if (isOnline && (!customerData || !addressData)) {
                const [{ data: addrData, error: addrError }, { data: custData, error: custError }] = await Promise.all([
                    order.address_id
                        ? supabase.from('customer_addresses').select('label, full_address').eq('address_id', order.address_id).maybeSingle()
                        : Promise.resolve({ data: null, error: null }),
                    order.user_id
                        ? supabase.from('customers').select('first_name, last_name, phone_number, email').eq('user_id', order.user_id).maybeSingle()
                        : Promise.resolve({ data: null, error: null })
                ])

                if (addrError) console.error('Fallback address fetch error:', addrError)
                if (custError) console.error('Fallback customer fetch error:', custError)

                addressData = addressData || addrData || null
                customerData = customerData || custData || null
            }

            setDeliveryInfo({ customer: customerData, address: addressData })
            setDeliveryInfoLoading(false)
        }

        console.log('order_source:', order.order_source, 'customer:', order.customer, 'address:', order.delivery_address)

        try {
            if (!isOnline) {
                const cached = await offlineDB.orderItemsCache.where('order_id').equals(order.order_id).toArray()
                setOrderItems(cached)
                setShowViewModal(true)
                return
            }

            const { data, error } = await supabase
                .from('order_items')
                .select(`
                    *,
                    menu_items ( name, category, item_img, classification ),
                    order_item_variants ( variant_id, variant_price, item_variants ( variant_id, variant_name, category ) ),
                    order_item_modifiers ( modifier_id, modifier_price, item_modifiers ( modifier_id, modifier_name, category ) )
                `)
                .eq('order_id', order.order_id)

            if (error) throw error
            setOrderItems(data || [])

            await offlineDB.orderItemsCache.where('order_id').equals(order.order_id).delete()
            await offlineDB.orderItemsCache.bulkPut((data || []).map(i => ({ ...i, order_id: order.order_id })))
        } catch (err) {
            console.error('Error fetching order items:', err)
            // Online fetch failed (flaky connection) — fall back to cache instead of showing nothing
            const cached = await offlineDB.orderItemsCache.where('order_id').equals(order.order_id).toArray()
            setOrderItems(cached)
        }

        setShowViewModal(true)
    }

    const handleMarkItemReady = async (orderItemId, orderId) => {
        try {
            if (viewingOrder?.order_status !== 'preparing') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Order Not In Preparation',
                    text: 'You can only mark items as ready when the order is already under preparing.',
                    confirmButtonColor: '#A0826D'
                })
                return
            }

            if (!isOnline) {
                await enqueueAction('mark_item_ready', {
                    orderItemId: orderItemId,
                    orderClientRef: viewingOrder?.client_ref,
                    orderId: viewingOrder?.order_id,
                    staffId: staffId
                })

                await offlineDB.orderItemsCache
                    .where('order_item_id').equals(orderItemId)
                    .modify({ prep_status: 'ready' })

                const updatedItems = orderItems.map(i =>
                    i.order_item_id === orderItemId ? { ...i, prep_status: 'ready' } : i
                )
                setOrderItems(updatedItems)

                // Check if every active (non-voided) item is now ready — if so,
                // mirror what syncOrderStatusFromItems does online, but locally.
                const activeItems = updatedItems.filter(
                    i => !['voided', 'void_approved'].includes(String(i.line_status || '').toLowerCase())
                )
                const allReady = activeItems.length > 0 && activeItems.every(
                    i => String(i.prep_status || '').toLowerCase() === 'ready'
                )

                if (allReady) {
                    setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: 'ready' } : o))
                }

                Swal.fire({
                    icon: 'info',
                    title: 'Queued Offline',
                    text: 'This will sync once wifi returns.',
                    confirmButtonColor: '#A0826D',
                    timer: 1800
                })
                return
            }

            const { error } = await supabase
                .from('order_items')
                .update({ 
                    prep_status: 'ready',
                    processed_by: staffId
                })
                .eq('order_item_id', orderItemId)   

            if (error) throw error

            await syncOrderStatusFromItems(orderId)

            const { data: refreshedOrder, error: refreshedOrderError } = await supabase
                .from('orders')
                .select(`
                    *,
                    barista_info:barista_info!orders_order_completed_by_fkey ( barista_id, first_name, last_name ),
                    delivery_address:customer_addresses!orders_address_id_fkey ( label, full_address ),
                    customer:customers!orders_user_id_fkey ( first_name, last_name, phone_number, email )
                `)
                .eq('order_id', orderId)
                .maybeSingle()

            if (refreshedOrderError) throw refreshedOrderError

            await fetchOrders(branchId)

            if (refreshedOrder && viewingOrder?.order_id === orderId) {
                await handleViewOrder(refreshedOrder)
            }

            if (refreshedOrder?.order_status === 'ready') {
                Swal.fire({
                    icon: 'success',
                    title: 'Order Ready',
                    text: 'All items are ready. Review the order, then mark it as completed.',
                    confirmButtonColor: '#A0826D',
                    timer: 2000
                })
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Item Updated',
                    text: 'The item has been marked as ready.',
                    confirmButtonColor: '#A0826D',
                    timer: 1400
                })
            }
        } catch (err) {
            console.error('Error marking item ready:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Update Item',
                text: err.message || 'An error occurred while updating the item.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

    const handleBulkMarkAllReady = async (orderId) => {
        try {
            if (viewingOrder?.order_status !== 'preparing') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Order Not In Preparation',
                    text: 'You can only mark all items as ready when the order is under preparation.',
                    confirmButtonColor: '#A0826D'
                })
                return
            }

            const result = await Swal.fire({
                title: 'Mark All Items Ready?',
                text: 'This will mark all items in this order as ready.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#A0826D',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Yes, mark all',
                cancelButtonText: 'Cancel'
            })

            if (!result.isConfirmed) return

            if (!isOnline) {
                const validItems = orderItems.filter(
                    item =>
                        !['voided', 'void_approved'].includes(String(item.line_status || '').toLowerCase()) &&
                        String(item.prep_status || '').toLowerCase() !== 'ready'
                )

                if (validItems.length === 0) return

                for (const item of validItems) {
                    await enqueueAction('mark_item_ready', {
                        orderItemId: item.order_item_id,
                        orderClientRef: viewingOrder?.client_ref,
                        orderId: viewingOrder?.order_id,
                        staffId: staffId
                    })
                }

                const validItemIds = validItems.map(i => i.order_item_id)
                await offlineDB.orderItemsCache
                    .where('order_item_id').anyOf(validItemIds)
                    .modify({ prep_status: 'ready' })

                setOrderItems(prev => prev.map(i =>
                    validItems.some(v => v.order_item_id === i.order_item_id) ? { ...i, prep_status: 'ready' } : i
                ))
                setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: 'ready' } : o))

                Swal.fire({
                    icon: 'info',
                    title: 'Queued Offline',
                    text: 'All items will be marked ready once wifi returns.',
                    confirmButtonColor: '#A0826D',
                    timer: 1800
                })
                return
            }

            const { data: items, error } = await supabase
                .from('order_items')
                .select('order_item_id, line_status, prep_status')
                .eq('order_id', orderId)

            if (error) throw error

            const validItems = (items || []).filter(
                item =>
                    !['voided', 'void_approved'].includes(String(item.line_status || '').toLowerCase()) &&
                    String(item.prep_status || '').toLowerCase() !== 'ready'
            )

            const itemIds = validItems.map(i => i.order_item_id)

            if (itemIds.length === 0) return

            const { error: updateError } = await supabase
                .from('order_items')
                .update({ 
                    prep_status: 'ready',
                    processed_by: staffId
                })
                .in('order_item_id', itemIds)

            if (updateError) throw updateError

            await syncOrderStatusFromItems(orderId)

            const { data: refreshedOrder, error: refreshedOrderError } = await supabase
                .from('orders')
                .select(`
                    *,
                    barista_info:barista_info!orders_order_completed_by_fkey ( barista_id, first_name, last_name ),
                    delivery_address:customer_addresses!orders_address_id_fkey ( label, full_address ),
                    customer:customers!orders_user_id_fkey ( first_name, last_name, phone_number, email )
                `)
                .eq('order_id', orderId)
                .maybeSingle()

            if (refreshedOrderError) throw refreshedOrderError

            await fetchOrders(branchId)

            if (refreshedOrder && viewingOrder?.order_id === orderId) {
                await handleViewOrder(refreshedOrder)
            }

            if (refreshedOrder?.order_status === 'ready') {
                Swal.fire({
                    icon: 'success',
                    title: 'Order Ready',
                    text: 'All items are ready. Review the order, then mark it as completed.',
                    confirmButtonColor: '#A0826D',
                    timer: 2000
                })
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'All Items Ready',
                    text: 'All items in this order are now ready.',
                    confirmButtonColor: '#A0826D',
                    timer: 1500
                })
            }

        } catch (err) {
            console.error(err)
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: err.message || 'Something went wrong.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

        // Award loyalty points when an order is actually completed by the barista.
    // Idempotent: keyed on order_id in points_history, so a retry / re-complete
    // (or a backend that already credited this order) can't double-credit.
    const awardLoyaltyPointsForOrder = async (order) => {
        try {
            if (!order?.user_id) return null   // walk-in / no linked loyalty account

            const pointsEarned = Math.floor(parseFloat(order.final_amount || 0) / 20)
            if (pointsEarned <= 0) return null

            // Already credited?
            const { data: existing } = await supabase
                .from('points_history')
                .select('points_id')
                .eq('order_id', order.order_id)
                .eq('transaction_type', 'earned')
                .limit(1)

            if (existing && existing.length > 0) return null

            const { data: account, error: accountError } = await supabase
                .from('loyalty_accounts')
                .select('loyalty_id, points_balance, points_earned_this_period, current_tier_id')
                .eq('user_id', order.user_id)
                .maybeSingle()

            if (accountError) throw accountError
            if (!account) return null   // customer has no loyalty account yet

            const { error: historyError } = await supabase
                .from('points_history')
                .insert([{
                    loyalty_id: account.loyalty_id,
                    order_id: order.order_id,
                    points_amount: pointsEarned,
                    transaction_type: 'earned',
                    description: `Order #${order.reference_number || order.order_id} completed`,
                }])
            if (historyError) throw historyError

            const newBalance = Number(account.points_balance || 0) + pointsEarned
            const newPeriodPoints = Number(account.points_earned_this_period || 0) + pointsEarned

            // Recompute tier from the new balance so the mobile card updates too.
            const { data: tiers } = await supabase
                .schema('marketing')
                .from('loyalty_tiers')
                .select('tier_id')
                .eq('is_archived', false)
                .lte('points_required', Math.floor(newBalance))
                .order('tier_level', { ascending: false })
                .limit(1)

            const newTierId = tiers && tiers.length > 0 ? tiers[0].tier_id : account.current_tier_id

            const { error: updateError } = await supabase
                .from('loyalty_accounts')
                .update({
                    points_balance: newBalance,
                    points_earned_this_period: newPeriodPoints,
                    current_tier_id: newTierId || account.current_tier_id,
                })
                .eq('loyalty_id', account.loyalty_id)
            if (updateError) throw updateError

            return pointsEarned
        } catch (err) {
            console.error('Failed to award loyalty points on completion:', err)
            return null
        }
    }

    const handleStatusChange = async (orderId, newStatus) => {
        const order = orders.find(o => o.order_id === orderId)

        if (isPaymentPending(order)) {
            Swal.fire({
                icon: 'warning',
                title: 'Payment Not Confirmed',
                text: 'This order is still awaiting online payment confirmation and cannot be advanced yet.',
                confirmButtonColor: '#A0826D'
            })
            return
        }

        const result = await Swal.fire({
            title: 'Update Order Status?',
            text: `Change order status to ${newStatus}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#A0826D',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, update',
            cancelButtonText: 'Cancel'
        })

        if (!result.isConfirmed) return

        if (!isOnline) {
            try {
                const completedAtIso = newStatus === 'completed' ? new Date().toISOString() : null
                const completedByBaristaId = newStatus === 'completed' ? (selectedBarista?.barista_id ?? null) : null
                // Offline orders never went through the online join, so stash a
                // display-ready barista_info shape here so the View Modal's
                // "Completed By" field has something to read.
                const completedByInfo = newStatus === 'completed' && selectedBarista
                    ? { barista_id: selectedBarista.barista_id, first_name: selectedBarista.first_name, last_name: selectedBarista.last_name }
                    : null

                await enqueueAction('update_status', {
                    orderClientRef: order.client_ref,
                    orderId: order.order_id,
                    newStatus,
                    baristaId: selectedBarista?.barista_id ?? null,
                    completedBy: completedByBaristaId,
                })

                if (newStatus === 'preparing') {
                    await offlineDB.orderItemsCache.where('order_id').equals(orderId).modify(item => {
                        if (item.prep_status === 'pending' && item.line_status !== 'voided' && item.line_status !== 'void_approved') {
                            item.prep_status = 'preparing'
                        }
                    })
                    setOrderItems(prev => prev.map(i =>
                        i.prep_status === 'pending' && i.line_status !== 'voided' && i.line_status !== 'void_approved'
                            ? { ...i, prep_status: 'preparing' }
                            : i
                    ))
                }

                // Persist to the same cache TransactionPrinter reads from offline,
                // otherwise its copy of this order stays on the old status.
                await offlineDB.ordersCache.where('order_id').equals(orderId).modify({
                    order_status: newStatus,
                    updated_at: new Date().toISOString(),
                    ...(newStatus === 'completed' ? {
                        completed_at: completedAtIso,
                        order_completed_by: completedByBaristaId,
                        barista_info: completedByInfo,
                    } : {}),
                })

                setOrders(prev => prev.map(o => o.order_id === orderId ? {
                    ...o,
                    order_status: newStatus,
                    ...(newStatus === 'completed' ? {
                        completed_at: completedAtIso,
                        order_completed_by: completedByBaristaId,
                        barista_info: completedByInfo,
                    } : {}),
                } : o))

                // Tell every other screen listening (TransactionPrinter included) to refresh now.
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('kaperk:orders-changed'))
                }

                Swal.fire({
                    icon: 'info',
                    title: 'Queued Offline',
                    text: 'Status change will sync once wifi returns.',
                    confirmButtonColor: '#A0826D',
                    timer: 1800
                })
            } catch (err) {
                console.error('Error queuing status change:', err)
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Queue Update',
                    text: err.message || 'An error occurred while queuing the status change.',
                    confirmButtonColor: '#A0826D'
                })
            }
            return
        }

        try {
            const updateData = {
                order_status: newStatus,
                updated_at: new Date().toISOString(),
                barista_id: selectedBarista?.barista_id ?? null
            }

            if (newStatus === 'completed') {
                updateData.completed_at = new Date().toISOString()
                updateData.order_completed_by = selectedBarista?.barista_id ?? null
            }

            const { data, error } = await supabase
                .from('orders')
                .update(updateData)
                .eq('order_id', orderId)
                .eq('branch_id', branchId)
                .select('order_id, order_status, branch_id')

            if (error) throw error

            if (!data || data.length === 0) {
                throw new Error('No rows were updated. This usually means the update was blocked by RLS/policies or the row did not match the filters.')
            }

            if (newStatus === 'completed') {
                const { error: prepError } = await supabase
                    .from('order_items')
                    .update({ prep_status: 'completed' })
                    .eq('order_id', orderId)
                    .in('prep_status', ['pending', 'ready', 'preparing'])
                    .not('line_status', 'in', '("voided","void_approved")')

                if (prepError) {
                    console.error('Failed to mark items as completed:', prepError.message)
                }

                const { error: inventoryError } = await supabase
                    .rpc('consume_reserved_stock', { p_order_id: orderId })

                if (inventoryError) {
                    throw new Error(inventoryError.message || 'Order was completed, but reserved stock was not deducted.')
                }

                // Points are credited here — not at checkout — so the customer's
                // balance only moves once the order is actually fulfilled.
                const pointsAwarded = await awardLoyaltyPointsForOrder(order)
                if (pointsAwarded) {
                    console.log(`[OrderManagement] awarded ${pointsAwarded} pts for order ${orderId}`)
                }
            } else if (newStatus === 'preparing') {
                const { error: prepStartError } = await supabase
                    .from('order_items')
                    .update({ prep_status: 'preparing' })
                    .eq('order_id', orderId)
                    .eq('prep_status', 'pending')
                    .not('line_status', 'in', '("voided","void_approved")')

                if (prepStartError) {
                    console.error('Failed to mark items as preparing:', prepStartError.message)
                }
            }

            const updatedOrder = data[0]

            await fetchOrders(branchId)

            Swal.fire({
                icon: 'success',
                title: 'Status Updated',
                text: `Order status changed to ${updatedOrder.order_status}.`,
                confirmButtonColor: '#A0826D',
                timer: 1800
            })
        } catch (err) {
            console.error('Error updating order status:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Update Status',
                text: err.message || 'An error occurred while updating the order status.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

    const handleCancelOrder = async (orderId) => {
        const order = orders.find(o => o.order_id === orderId)

        if (!['pending', 'preparing'].includes(order?.order_status)) {
            Swal.fire({
                icon: 'warning',
                title: 'Cannot Cancel Order',
                text: 'Only pending or preparing orders can be cancelled.',
                confirmButtonColor: '#A0826D'
            })
            return
        }

        const result = await Swal.fire({
            title: 'Cancel This Order?',
            text: 'This will cancel the order and void all active items. This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DC2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, cancel order',
            cancelButtonText: 'Go back'
        })

        if (!result.isConfirmed) return

        // ── OFFLINE PATH ────────────────────────────────────────────────
        if (!isOnline) {
            try {
                await enqueueAction('cancel_order', {
                    orderClientRef: order.client_ref,
                    orderId: order.order_id,
                    branchId,
                })

                // Mirror what syncCancelOrder will do once back online, so the
                // cached view stays consistent in the meantime.
                await offlineDB.ordersCache.where('order_id').equals(orderId).modify({
                    order_status: 'cancelled',
                    updated_at: new Date().toISOString(),
                })

                await offlineDB.orderItemsCache.where('order_id').equals(orderId).modify(item => {
                    if (item.line_status === 'active') {
                        item.line_status = 'voided'
                        item.void_reason = 'order_cancelled'
                    }
                })

                setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: 'cancelled' } : o))

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('kaperk:orders-changed'))
                }

                if (viewingOrder?.order_id === orderId) {
                    setShowViewModal(false)
                    setViewingOrder(null)
                    setOrderItems([])
                    setShowVoidedItems(false)
                }

                Swal.fire({
                    icon: 'info',
                    title: 'Queued Offline',
                    text: 'This order will be cancelled once wifi returns.',
                    confirmButtonColor: '#A0826D',
                    timer: 1800
                })
            } catch (err) {
                console.error('Error queuing order cancellation:', err)
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Queue Cancellation',
                    text: err.message || 'An error occurred while queuing the cancellation.',
                    confirmButtonColor: '#A0826D'
                })
            }
            return
        }

        try {
            // ── Fetch active items (include prep_status to filter out already-ready items) ──
            const { data: activeItems, error: fetchError } = await supabase
                .from('order_items')
                .select('order_item_id, item_id, quantity, prep_status')
                .eq('order_id', orderId)
                .eq('line_status', 'active')

            if (fetchError) throw fetchError

            // ── Void all active items ──────────────────────────────────────────
            if (activeItems && activeItems.length > 0) {
                const activeIds = activeItems.map(i => i.order_item_id)

                const { error: itemsError } = await supabase
                    .from('order_items')
                    .update({
                        line_status: 'voided',
                        void_reason: 'order_cancelled'
                    })
                    .in('order_item_id', activeIds)

                if (itemsError) throw itemsError
            }

            // ── Release reservations only for items NOT yet ready ──────────────
            //
            // Items already marked 'ready' have been physically set aside — their
            // stock was already pulled. Only release for still-pending items.
            const notReadyItems = (activeItems || []).filter(
                item => String(item.prep_status || '').toLowerCase() !== 'ready'
            )

            if (notReadyItems.length > 0) {
                const notReadyOrderItemIds = notReadyItems.map(i => i.order_item_id)

                // Fetch reservations directly by order_item_id — no recipe lookup needed.
                // This is the source of truth: one reservation row per ingredient per order item.
                const { data: reservationRows, error: resFetchError } = await supabase
                    .schema('inventory')
                    .from('stock_reservations')
                    .select('reservation_id, ingredient_id, reserved_quantity, unit')
                    .eq('order_id', orderId)
                    .eq('branch_id', branchId)
                    .eq('status', 'reserved')
                    .in('order_item_id', notReadyOrderItemIds)

                if (resFetchError) throw resFetchError

                if (reservationRows && reservationRows.length > 0) {
                    const reservationIds = reservationRows.map(r => r.reservation_id)

                    // Mark all matched reservations as returned
                    const { error: releaseError } = await supabase
                        .schema('inventory')
                        .from('stock_reservations')
                        .update({ status: 'returned' })
                        .in('reservation_id', reservationIds)

                    if (releaseError) throw releaseError

                    // Build aggregated release map: ingredient_id { qty, unit }
                    // (one order might have multiple items using the same ingredient)
                    const releaseMap = new Map()
                    for (const row of reservationRows) {
                        const prev = releaseMap.get(row.ingredient_id) || { qty: 0, unit: row.unit }
                        releaseMap.set(row.ingredient_id, {
                            qty: prev.qty + Number(row.reserved_quantity || 0),
                            unit: row.unit
                        })
                    }

                    // Log one inventory_transaction per ingredient returned
                    const staffId = sessionStorage.getItem('staffId') || null
                    const now = new Date().toISOString()

                    for (const [ingredientId, { qty, unit }] of releaseMap.entries()) {
                        const { error: txError } = await supabase
                            .schema('inventory')
                            .from('inventory_transactions')
                            .insert([{
                                branch_id: branchId,
                                ingredient_id: ingredientId,
                                transaction_type: 'adjustment',
                                quantity: qty,
                                unit: unit || '',
                                reference_type: 'order_cancellation',
                                reference_id: null,
                                notes: `Stock returned: order #${order?.reference_number || orderId} was cancelled`,
                                created_by: staffId,
                                created_at: now
                            }])

                        if (txError) throw txError
                    }
                }
            }

            // ── Cancel pending payment transactions ────────────────────────────
            const { error: payTxError } = await supabase
                .from('order_transactions')
                .update({
                    status: 'cancelled',
                    updated_at: new Date().toISOString()
                })
                .eq('order_id', orderId)
                .eq('status', 'pending')

            if (payTxError) console.warn('Transaction cancel warning:', payTxError.message)

            // ── Cancel the order itself ────────────────────────────────────────
            const { data, error: orderError } = await supabase
                .from('orders')
                .update({
                    order_status: 'cancelled',
                    updated_at: new Date().toISOString()
                })
                .eq('order_id', orderId)
                .eq('branch_id', branchId)
                .select('order_id, order_status')

            if (orderError) throw orderError

            if (!data || data.length === 0) {
                throw new Error(
                    'Order update returned no rows. Check that branch_id matches or that RLS policies allow this update.'
                )
            }

            if (viewingOrder?.order_id === orderId) {
                setShowViewModal(false)
                setViewingOrder(null)
                setOrderItems([])
                setShowVoidedItems(false)
            }

            await fetchOrders(branchId)

            Swal.fire({
                icon: 'success',
                title: 'Order Cancelled',
                text: 'The order has been cancelled and reserved stock has been returned.',
                confirmButtonColor: '#A0826D',
                timer: 1800
            })
        } catch (err) {
            console.error('Error cancelling order:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Cancel Order',
                text: err.message || 'An error occurred while cancelling the order.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

    const verifyCancelPin = async () => {
        if (!cancelPinInput || cancelPinInput.length < 4) {
            Swal.fire({ icon: 'warning', title: 'PIN Required', text: 'Please enter a valid Admin PIN.', confirmButtonColor: '#A0826D' })
            return
        }

        const bcrypt = (await import('bcryptjs')).default

        // ── OFFLINE PATH ──
        if (!isOnline) {
            const cached = await offlineDB.adminPinCache.get(branchId)
            const pinHashes = cached?.pinHashes || []

            if (pinHashes.length === 0) {
                Swal.fire({ icon: 'warning', title: 'No Cached Admin PIN', text: 'No Admin PIN was cached for offline use. Please reconnect once before going offline.', confirmButtonColor: '#A0826D' })
                return
            }

            const matchResults = await Promise.all(pinHashes.map(hash => bcrypt.compare(cancelPinInput, hash)))
            if (!matchResults.some(Boolean)) {
                Swal.fire({ icon: 'error', title: 'Invalid PIN', text: 'The PIN you entered is incorrect.', confirmButtonColor: '#DC2626' })
                return
            }

            setShowCancelPinModal(false)
            setCancelPinInput('')
            await handleCancelOrder(pendingCancelOrderId)
            setPendingCancelOrderId(null)
            return
        }

        const { data: adminStaffList, error: adminStaffError } = await supabase
            .from('staff')
            .select('id, email, company_email')
            .eq('branch_id', branchId)
            .eq('role', 'Admin')

        if (adminStaffError || !adminStaffList?.length) {
            Swal.fire({ icon: 'warning', title: 'No Admin Found', text: 'No Admin is assigned to this branch.', confirmButtonColor: '#A0826D' })
            return
        }

        const adminStaffIds = adminStaffList.map(s => s.id)
        const adminEmails = adminStaffList.flatMap(s => [s.email, s.company_email]).filter(Boolean)

        const orParts = [`staff_id.in.(${adminStaffIds.join(',')})`]
        if (adminEmails.length) orParts.push(`email.in.(${adminEmails.join(',')})`)

        const { data: adminAccounts, error: accountError } = await supabase
            .from('staff_accounts')
            .select('pin_hash')
            .or(orParts.join(','))

        console.log('[verifyCancelPin] adminStaffList:', adminStaffList, 'adminAccounts found:', adminAccounts)

        if (accountError) { Swal.fire({ icon: 'error', title: 'Error', text: accountError.message, confirmButtonColor: '#A0826D' }); return }

        const { data: trueAdminAccounts, error: trueAdminError } = await supabase
            .from('admin_accounts')
            .select('pin_hash')

        if (trueAdminError) { Swal.fire({ icon: 'error', title: 'Error', text: trueAdminError.message, confirmButtonColor: '#A0826D' }); return }

        const validHashes = [
            ...(adminAccounts || []),
            ...(trueAdminAccounts || []),
        ].map(a => a.pin_hash).filter(Boolean)

        if (!validHashes.length) {
            Swal.fire({ icon: 'warning', title: 'No PIN Set', text: 'No Admin in this branch has set a PIN yet.', confirmButtonColor: '#A0826D' })
            return
        }

        console.log('[verifyCancelPin] cancelPinInput:', JSON.stringify(cancelPinInput), 'validHashes:', validHashes)
        const matchResults = await Promise.all(validHashes.map(hash => bcrypt.compare(cancelPinInput, hash)))
        console.log('[verifyCancelPin] matchResults:', matchResults)
        const isMatch = matchResults.some(Boolean)

        if (!isMatch) {
            Swal.fire({ icon: 'error', title: 'Invalid PIN', text: 'The PIN you entered is incorrect.', confirmButtonColor: '#DC2626' })
            return
        }

        setShowCancelPinModal(false)
        setCancelPinInput('')
        await handleCancelOrder(pendingCancelOrderId)
        setPendingCancelOrderId(null)
    }

    const syncOrderStatusFromItems = async (orderId) => {
        const { data: items, error: itemsError } = await supabase
            .from('order_items')
            .select('order_item_id, prep_status, line_status')
            .eq('order_id', orderId)

        if (itemsError) throw itemsError

        const activeItems = (items || []).filter(
            item => !['voided', 'void_approved'].includes(String(item.line_status || '').toLowerCase())
        )

        if (activeItems.length === 0) return

        const allReady = activeItems.every(
            item => String(item.prep_status || '').toLowerCase() === 'ready'
        )

        // When all items are ready → complete the order automatically
        // When only some are ready → keep as preparing
        // "ready" is a real checkpoint — staff must manually confirm before
        // the order moves to "completed" (see handleStatusChange).
        const nextOrderStatus = allReady ? 'ready' : 'preparing'

        const { data: updatedOrders, error: orderUpdateError } = await supabase
            .from('orders')
            .update({
                order_status: nextOrderStatus,
                updated_at: new Date().toISOString()
            })
            .eq('order_id', orderId)
            .select('order_id, order_status')

        if (orderUpdateError) throw orderUpdateError

        if (!updatedOrders || updatedOrders.length === 0) {
            throw new Error('Failed to sync order status from item readiness.')
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800'
            case 'preparing':
                return 'bg-blue-100 text-blue-800'
            case 'ready':
                return 'bg-purple-100 text-purple-800'
            case 'completed':
                return 'bg-green-100 text-green-800'
            case 'cancelled':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    const getColumnTheme = (status) => {
        switch (status) {
            case 'pending':
                return {
                    accent: 'border-t-4 border-yellow-400',
                    badge: 'bg-yellow-100 text-yellow-700',
                    button: 'bg-yellow-400 hover:bg-yellow-500 text-white',
                    iconBg: 'bg-yellow-100 text-yellow-600',
                    countBg: 'bg-yellow-100 text-yellow-700'
                }
            case 'preparing':
                return {
                    accent: 'border-t-4 border-blue-400',
                    badge: 'bg-blue-100 text-blue-700',
                    button: 'bg-blue-400 hover:bg-blue-500 text-white',
                    iconBg: 'bg-blue-100 text-blue-600',
                    countBg: 'bg-blue-100 text-blue-700'
                }
            case 'ready':
                return {
                    accent: 'border-t-4 border-green-500',
                    badge: 'bg-green-100 text-green-700',
                    button: 'bg-green-500 hover:bg-green-600 text-white',
                    iconBg: 'bg-green-100 text-green-600',
                    countBg: 'bg-green-100 text-green-700'
                }
            case 'completed':
                return {
                    accent: 'border-t-4 border-gray-400',
                    badge: 'bg-gray-100 text-gray-700',
                    button: 'bg-gray-300 hover:bg-gray-400 text-white',
                    iconBg: 'bg-gray-100 text-gray-500',
                    countBg: 'bg-gray-100 text-gray-700'
                }
            case 'cancelled':
                return {
                    accent: 'border-t-4 border-red-300',
                    badge: 'bg-red-100 text-red-700',
                    button: 'bg-red-100 hover:bg-red-200 text-red-700',
                    iconBg: 'bg-red-100 text-red-500',
                    countBg: 'bg-red-100 text-red-700'
                }
            default:
                return {
                    accent: 'border-t-4 border-gray-300',
                    badge: 'bg-gray-100 text-gray-700',
                    button: 'bg-gray-300 hover:bg-gray-400 text-white',
                    iconBg: 'bg-gray-100 text-gray-500',
                    countBg: 'bg-gray-100 text-gray-700'
                }
        }
    }

    const getNextStatus = (currentStatus) => {
        const statusFlow = {
            pending: 'preparing',
            preparing: null,
            ready: 'completed',
            completed: null,
            cancelled: null
        }

        return statusFlow[currentStatus] || null
    }

    // Cash orders (and any order with no transaction row, e.g. legacy data)
    // are never blocked. Online orders (gcash/card/etc.) are blocked until
    // their order_transactions row reads 'paid'.
    const isPaymentPending = (order) => {
        const tx = order?.order_transactions?.[0]
        if (!tx) return false
        if (String(tx.payment_method || '').toLowerCase() === 'cash') return false
        return tx.status !== 'paid'
    }

    const formatCurrency = (amount) => {
        return `₱${parseFloat(amount || 0).toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'

        return new Date(dateString).toLocaleString('en-PH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        })
    }

    const formatFulfillmentType = (value) => {
        switch (value) {
            case 'dine_in':
                return 'Dine-In'
            case 'takeout':
                return 'Takeout'
            case 'pickup':
                return 'Pickup'
            case 'delivery':
                return 'Delivery'
            default:
                return 'N/A'
        }
    }

    const formatOrderSource = (value) => {
        switch (value) {
            case 'pos':
                return 'POS'
            case 'mobile_app':
                return 'Mobile App'
            default:
                return 'N/A'
        }
    }

    const formatCardTime = (dateString) => {
        if (!dateString) return '--:--'
        return new Date(dateString).toLocaleTimeString('en-PH', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        })
    }

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const search = searchQuery.toLowerCase()

            const matchesSearch =
                String(order.reference_number || '').toLowerCase().includes(search) ||
                String(order.order_id || '').toLowerCase().includes(search) ||
                formatOrderSource(order.order_source).toLowerCase().includes(search) ||
                (order.order_source === 'mobile_app' ? 'mobile customer' : 'walk-in customer').includes(search)

            const matchesStatus =
                filterStatus === 'All' || order.order_status === filterStatus.toLowerCase()

            let matchesDate = true

            if (dateFilter !== 'all') {
                const orderDate = new Date(order.created_at)
                const now = new Date()

                if (dateFilter === 'day') {
                    matchesDate =
                        orderDate.toDateString() === now.toDateString()
                }

                if (dateFilter === 'week') {
                    const startOfWeek = new Date(now)
                    startOfWeek.setDate(now.getDate() - now.getDay())

                    matchesDate = orderDate >= startOfWeek
                }

                if (dateFilter === 'month') {
                    matchesDate =
                        orderDate.getMonth() === now.getMonth() &&
                        orderDate.getFullYear() === now.getFullYear()
                }

                if (dateFilter === 'year') {
                    matchesDate =
                        orderDate.getFullYear() === now.getFullYear()
                }
                if (dateFilter === 'custom' && customStart && customEnd) {
                    const from = new Date(customStart)
                    const to   = new Date(customEnd)
                    to.setHours(23, 59, 59, 999)
                    matchesDate = orderDate >= from && orderDate <= to
                }
                if (dateFilter === 'custom' && (!customStart || !customEnd)) {
                    matchesDate = true
                }
            }

            return matchesSearch && matchesStatus && matchesDate
        })
    }, [orders, searchQuery, filterStatus, dateFilter, customStart, customEnd])


    const groupedOrders = useMemo(() => {
        // FIFO for active/incoming statuses — oldest order first, since these
        // are a queue staff need to work through in the order they came in.
        const byOldestFirst = (a, b) => new Date(a.created_at) - new Date(b.created_at)
        // Completed/cancelled are a log, not a queue — newest first so staff
        // see what just happened at the top.
        const byNewestFirst = (a, b) => new Date(b.created_at) - new Date(a.created_at)

        return {
            pending: filteredOrders.filter(order => order.order_status === 'pending').sort(byOldestFirst),
            preparing: filteredOrders.filter(order => order.order_status === 'preparing').sort(byOldestFirst),
            ready: filteredOrders.filter(order => order.order_status === 'ready').sort(byOldestFirst),
            completed: filteredOrders.filter(order => order.order_status === 'completed').sort(byNewestFirst),
            cancelled: filteredOrders.filter(order => order.order_status === 'cancelled').sort(byNewestFirst)
        }
    }, [filteredOrders])

    const visibleColumns = useMemo(() => {
        if (filterStatus === 'All') {
            return [
                { title: 'Pending', key: 'pending', orders: groupedOrders.pending },
                { title: 'Preparing', key: 'preparing', orders: groupedOrders.preparing },
                { title: 'Ready', key: 'ready', orders: groupedOrders.ready },
                { title: 'Completed', key: 'completed', orders: groupedOrders.completed },
            ]
        }

        const selectedKey = filterStatus.toLowerCase()
            return [
                {
                    title: filterStatus,
                    key: selectedKey,
                    orders: groupedOrders[selectedKey] || []
                }
            ]
    }, [filterStatus, groupedOrders])

    const stats = useMemo(() => {
        const totalOrders = filteredOrders.length
        const pendingCount = filteredOrders.filter(order => order.order_status === 'pending').length
        const preparingCount = filteredOrders.filter(order => order.order_status === 'preparing').length
        const readyCount = filteredOrders.filter(order => order.order_status === 'ready').length
        const completedCount = filteredOrders.filter(order => order.order_status === 'completed').length
        const cancelledCount = filteredOrders.filter(order => order.order_status === 'cancelled').length
        const totalRevenue = filteredOrders
            .filter(order => order.order_status === 'completed')
            .reduce((sum, order) => sum + parseFloat(order.final_amount || 0), 0)

        return {
            totalOrders,
            pendingCount,
            preparingCount,
            readyCount,
            completedCount,
            cancelledCount,
            totalRevenue
        }
    }, [filteredOrders])

    // Overall Order Cards
    const renderOrderColumn = (title, statusKey, ordersList, page, setPage) => {
        const pagedList = ordersList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
        const theme = getColumnTheme(statusKey)
        
        return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-auto max-h-[70vh] xl:h-[620px]">
                <div className={`px-4 py-3 ${theme.accent}`}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme.countBg}`}>
                            {ordersList.length}
                        </span>
                    </div>
                </div>

                <div className="p-3 space-y-3 flex-1 overflow-y-auto bg-gray-50/60">
                    {ordersList.length === 0 ? (
                        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-4 text-center text-sm text-gray-400">
                            No orders
                        </div>
                    ) : (
                        pagedList.map(order => (
                            <div
                                key={order.order_id}
                                className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${theme.accent.replace('border-t-4', 'border-l-4')}`}
                            >
                                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-semibold text-gray-700 flex-shrink-0 flex items-center gap-1.5">
                                        #{order.reference_number || String(order.order_id).slice(0, 6)}
                                        {order.is_remake && (
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-200">
                                                REMAKE
                                            </span>
                                        )}
                                    </span>
                                    <div className="text-right">
                                        <p className="text-[11px] text-gray-400 leading-tight">
                                            {new Date(order.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                        <p className="text-[11px] text-gray-500 font-medium leading-tight">
                                            {formatCardTime(order.created_at)}
                                        </p>
                                    </div>
                                </div>

                                <div className="px-3 py-3 space-y-2">
                                    <div className="text-sm font-semibold text-gray-800">
                                        {order.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in Customer'}
                                        {/* <span className="text-gray-400 font-normal"> • </span>
                                        <span className="text-gray-600 font-medium">
                                            {formatFulfillmentType(order.fulfillment_type)}
                                        </span> */}
                                    </div>

                                    <div className="text-xs text-gray-500">
                                        {formatFulfillmentType(order.fulfillment_type)}
                                    </div>

                                    {order.fulfillment_type === 'delivery' && order.delivery_address && (
                                        <div className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-1 mt-1">
                                            <span className="font-medium">{order.delivery_address.label || 'Address'}:</span>{' '}
                                            {order.delivery_address.full_address}
                                        </div>
                                    )}

                                    {order.with_cutlery && (
                                        <div className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-md px-2 py-1 mt-1 flex items-center gap-1">
                                            🍴 <span className="font-medium">Cutlery requested</span>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-1">
                                        <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${theme.badge}`}>
                                            {order.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
                                        </span>
                                        <span className="text-sm font-bold text-gray-800">
                                            {formatCurrency(order.final_amount)}
                                        </span>
                                    </div>
                                </div>

                                <div className="px-3 pb-3 flex gap-2">
                                    <button
                                        onClick={() => handleViewOrder(order)}
                                        className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${theme.button}`}
                                    >
                                        View Details
                                    </button>

                                    {statusKey === 'completed' && canUpdateOrders && (
                                        <button
                                            onClick={() => handleOpenRemakeModal(order)}
                                            disabled={remakeRequestOrderIds.has(order.order_id)}
                                            className={`px-3 py-2 rounded-md text-xs font-semibold border ${
                                                remakeRequestOrderIds.has(order.order_id)
                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                    : 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100'
                                            }`}
                                        >
                                            {remakeRequestOrderIds.has(order.order_id) ? 'Remake Requested' : 'Request Remake'}
                                        </button>
                                    )}

                                     {canUpdateOrders && getNextStatus(order.order_status) && (
                                        isPaymentPending(order) ? (
                                            <span className="px-3 py-2 rounded-md text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
                                                Awaiting Payment
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => handleStatusChange(order.order_id, getNextStatus(order.order_status))}
                                                className="px-3 py-2 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            >
                                                Next
                                            </button>
                                        )
                                    )}

                                </div>
                            </div>
                        ))
                    )}

                    {/* <Pagination
                                    page={page}
                                    totalItems={ordersList.length}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={setPage}
                                /> */}
                </div>
            </div>
        )
    }

        // FIltered Order Cards

        const renderFilteredStatusGrid = (title, statusKey, ordersList, page, setPage) => {
            const theme = getColumnTheme(statusKey)
            const pagedList = ordersList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

        return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className={`px-4 py-3 ${theme.accent}`}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme.countBg}`}>
                            {ordersList.length}
                        </span>
                    </div>
                </div>

                <div className="p-4 bg-gray-50/60 min-h-[520px]">
                    {ordersList.length === 0 ? (
                        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-400">
                            No orders
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            {pagedList.map(order => (
                                <div
                                    key={order.order_id}
                                    className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${theme.accent.replace('border-t-4', 'border-l-4')}`}
                                >
                                    <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-semibold text-gray-700 flex-shrink-0 flex items-center gap-1.5">
                                            #{order.reference_number || String(order.order_id).slice(0, 6)}
                                            {order.is_remake && (
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-200">
                                                    REMAKE
                                                </span>
                                            )}
                                        </span>
                                        <div className="text-right">
                                            <p className="text-[11px] text-gray-400 leading-tight">
                                                {new Date(order.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                            <p className="text-[11px] text-gray-500 font-medium leading-tight">
                                                {formatCardTime(order.created_at)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="px-3 py-3 space-y-2">
                                        <div className="text-sm font-semibold text-gray-800">
                                            {order.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in Customer'}
                                            {/* <span className="text-gray-400 font-normal"> • </span>
                                            <span className="text-gray-600 font-medium">
                                                {formatFulfillmentType(order.fulfillment_type)}
                                            </span> */}
                                        </div>

                                        <div className="text-xs text-gray-500">
                                            {formatFulfillmentType(order.fulfillment_type)}
                                        </div>

                                        {order.fulfillment_type === 'delivery' && order.delivery_address && (
                                            <div className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-1 mt-1">
                                                <span className="font-medium">{order.delivery_address.label || 'Address'}:</span>{' '}
                                                {order.delivery_address.full_address}
                                            </div>
                                        )}

                                        {order.with_cutlery && (
                                            <div className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-md px-2 py-1 mt-1 flex items-center gap-1">
                                                🍴 <span className="font-medium">Cutlery requested</span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-1">
                                            <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${theme.badge}`}>
                                                {order.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
                                            </span>
                                            <span className="text-sm font-bold text-gray-800">
                                                {formatCurrency(order.final_amount)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="px-3 pb-3 flex gap-2">
                                        <button
                                            onClick={() => handleViewOrder(order)}
                                            className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${theme.button}`}
                                        >
                                            View Details
                                        </button>

                                        {getNextStatus(order.order_status) && (
                                            <button
                                                onClick={() => handleStatusChange(order.order_id, getNextStatus(order.order_status))}
                                                className="px-3 py-2 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            >
                                                Next
                                            </button>
                                        )}

                                    </div>
                                </div>
                            ))}
                        </div>

                        
                    )}
                </div>
                <Pagination
                    page={page}
                    totalItems={ordersList.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />
            </div>
        )
    }

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
                >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 16, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                        Next →
                    </button>
                </div>
            </div>
        )
    }

    const columnPageState = {
        pending: { page: pendingPage, setPage: setPendingPage },
        preparing: { page: preparingPage, setPage: setPreparingPage },
        ready: { page: readyPage, setPage: setReadyPage },
        completed: { page: completedPage, setPage: setCompletedPage },
        cancelled: { page: cancelledPage, setPage: setCancelledPage },
    }
        
    return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 bg-[#f6f6f7] min-h-screen">

            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-5 ${staffRole === 'Admin' ? 'xl:grid-cols-7' : 'xl:grid-cols-6'}`}>
                {[
                    { label: 'Total Orders', value: stats.totalOrders, bg: 'bg-orange-100', text: 'text-orange-600', icon: <path fill="currentColor" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2s-.9-2-2-2M1 2v2h2l3.6 7.59l-1.35 2.45c-.16.28-.25.61-.25.96c0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12l.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2z"/> },
                    { label: 'Pending', value: stats.pendingCount, bg: 'bg-yellow-100', text: 'text-yellow-600', icon: <PendingIcon /> },
                    { label: 'Preparing', value: stats.preparingCount, bg: 'bg-blue-100', text: 'text-blue-600', icon: <PreparingIcon /> },
                    { label: 'Ready', value: stats.readyCount, bg: 'bg-green-100', text: 'text-green-600', icon: <path fill="currentColor" d="m9.55 18l-5.7-5.7l1.425-1.425l4.275 4.275l9.175-9.175L20.15 7.4z"/> },
                    { label: 'Completed', value: stats.completedCount, bg: 'bg-gray-100', text: 'text-gray-600', icon: <path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4zM12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22"/> },
                    { label: 'Cancelled', value: stats.cancelledCount, bg: 'bg-red-100', text: 'text-red-500', icon: <path fill="currentColor" d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10s10-4.47 10-10S17.53 2 12 2m5 13.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12z"/> },
                    { label: 'Total Revenue', value: formatCurrency(stats.totalRevenue), bg: 'bg-purple-100', text: 'text-purple-600', icon: <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87c1.96 0 2.4-.98 2.4-1.59c0-.83-.44-1.61-2.67-2.14c-2.48-.6-4.18-1.62-4.18-3.67c0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87c-1.5 0-2.4.68-2.4 1.64c0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16"/>, adminOnly: true },
                ].filter(card => !card.adminOnly || staffRole === 'Admin').
                map(({ label, value, bg, text, icon }) => (
                    <div key={label} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-4 flex flex-col items-start gap-2 min-w-0 w-full">
                        <div className={`w-9 h-9 rounded-lg ${bg} ${text} flex items-center justify-center shrink-0`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">{icon}</svg>
                        </div>
                        <p className="text-[11px] text-gray-500 leading-tight truncate w-full">{label}</p>
                        {loading ? (
                            <SkeletonBlock className="h-6 w-12" />
                        ) : (
                            <p className="text-xl font-bold text-gray-800 leading-none break-words w-full">{value}</p>
                        )}
                    </div>
                ))}
            </div>

            {/* {pendingSyncOrders.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                    <p className="text-sm font-semibold text-blue-800 mb-2">
                        {pendingSyncOrders.length} order{pendingSyncOrders.length !== 1 ? 's' : ''} waiting to sync
                    </p>
                    <div className="space-y-2">
                        {pendingSyncOrders.map(action => (
                            <div key={action.localId} className="bg-white rounded-lg px-3 py-2 text-xs text-gray-600 flex justify-between">
                                <span>{action.payload.order.reference_number}</span>
                                <span>{formatCurrency(action.payload.order.final_amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )} */}

           <div className="sticky top-0 z-30 bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                    <div className="flex flex-col md:flex-row gap-3 flex-1">
                        <div className="relative w-full md:max-w-xs">
                            {/* Search */}                            <input
                                type="text"
                                placeholder="Search order # ..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6"/>
                                </svg>
                            </span>
                        </div>

                        {/* Status Filter */}
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Preparing">Preparing</option>
                            <option value="Ready">Ready</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>

                        {/* Day filter */}
                        <select
                            value={dateFilter}
                            onChange={(e) => {
                                setDateFilter(e.target.value)
                                if (e.target.value !== 'custom') {
                                    setCustomStart('')
                                    setCustomEnd('')
                                }
                            }}
                            className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        >
                            <option value="all">All Time</option>
                            <option value="day">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="year">This Year</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {dateFilter === 'custom' && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-400">From:</span>
                                <input
                                    type="date"
                                    value={customStart}
                                    max={customEnd || new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="px-3 py-2.5 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    style={{ borderColor: customStart && customEnd ? '#A0826D' : '#D1D5DB' }}
                                />
                                <span className="text-xs text-gray-400">To</span>
                                <input
                                    type="date"
                                    value={customEnd}
                                    min={customStart}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="px-3 py-2.5 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    style={{ borderColor: customStart && customEnd ? '#A0826D' : '#D1D5DB' }}
                                />
                                {customStart && customEnd && (
                                    <button
                                        onClick={() => { setCustomStart(''); setCustomEnd('') }}
                                        className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50"
                                    >
                                        ✕ Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="text-sm text-gray-500">
                        {branchName || 'Assigned Branch'}
                    </div>
                </div>
            </div>

            {/* Order Status Filter */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                        <div className="w-10 h-10 border-4 border-[#A0826D] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-sm text-gray-500">Loading orders...</p>
                    </div>
                </div>
            ) : filterStatus === 'All' ? (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                    {visibleColumns.map((column) => (
                        <div key={column.key}>
                            {renderOrderColumn(column.title, column.key, column.orders, columnPageState[column.key].page, columnPageState[column.key].setPage)}
                        </div>
                    ))}
                </div>
            ) : (
                renderFilteredStatusGrid(
                    visibleColumns[0]?.title || filterStatus,
                    visibleColumns[0]?.key || filterStatus.toLowerCase(),
                    visibleColumns[0]?.orders || [],
                    filteredPage,
                    setFilteredPage
                )
)}
        </div>

        {/* View Modal */}
        {showViewModal && viewingOrder && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="absolute inset-0 bg-black bg-opacity-50"
                    onClick={() => {
                        setShowViewModal(false)
                        setViewingOrder(null)
                        setOrderItems([])
                    }}
                ></div>

                <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Order #{viewingOrder.reference_number || 'No Reference'}
                        </p>
                    </div>

                    <div className="px-6 py-6 space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Order ID</label>
                                    <p className="text-gray-800 font-mono text-sm">{viewingOrder.order_id}</p>
                                </div>
                                   <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Reference Number</label>
                                    <p className="text-gray-800 font-medium flex items-center gap-2">
                                        {viewingOrder.reference_number || 'N/A'}
                                        {viewingOrder.is_remake && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-200">
                                                REMAKE
                                            </span>
                                        )}
                                    </p>
                                </div>                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(viewingOrder.order_status)}`}>
                                        {viewingOrder.order_status?.charAt(0).toUpperCase() + viewingOrder.order_status?.slice(1)}
                                    </span>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Order Source</label>
                                    <p className="text-gray-800">{formatOrderSource(viewingOrder.order_source)}</p>                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Fulfillment Type</label>
                                    <p className="text-gray-800">{formatFulfillmentType(viewingOrder.fulfillment_type)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Created At</label>
                                    <p className="text-gray-800">{formatDate(viewingOrder.created_at)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Completed At</label>
                                    <p className="text-gray-800">{viewingOrder.completed_at ? formatDate(viewingOrder.completed_at) : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Completed By</label>
                                    <p className="text-gray-800 text-sm">
                                        {viewingOrder.barista_info
                                            ? `${viewingOrder.barista_info.first_name} ${viewingOrder.barista_info.last_name}`
                                            : '—'}
                                    </p>
                                </div>
                            </div>

                            {viewingOrder.customer_notes && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-amber-800 mb-1">Customer Notes</p>
                                    <p className="text-sm text-amber-700">{viewingOrder.customer_notes}</p>
                                </div>
                            )}

                            {viewingOrder.with_cutlery && (
                                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-center gap-2">
                                    <span className="text-lg">🍴</span>
                                    <p className="text-sm font-semibold text-teal-800">Customer requested cutlery</p>
                                </div>
                            )}

                            {viewingOrder.fulfillment_type === 'delivery' && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-3">Delivery Information</h3>
                                    {deliveryInfoLoading ? (
                                        <p className="text-sm text-blue-600">Loading delivery details...</p>
                                    ) : (deliveryInfo?.customer || deliveryInfo?.address) ? (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-600 mb-0.5">Customer Name</label>
                                                    <p className="text-sm text-gray-800">
                                                        {deliveryInfo.customer.first_name} {deliveryInfo.customer.last_name}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-600 mb-0.5">Phone Number</label>
                                                    <p className="text-sm text-gray-800">{deliveryInfo.customer.phone_number || '—'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-600 mb-0.5">Email</label>
                                                    <p className="text-sm text-gray-800">{deliveryInfo.customer.email || '—'}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-blue-600 mb-1">Delivery Address</label>
                                                {deliveryInfo.address ? (
                                                    <div className="bg-white border border-blue-100 rounded-md px-3 py-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-semibold text-gray-700">{deliveryInfo.address.label}</span>
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-0.5">{deliveryInfo.address.full_address}</p>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-gray-500">No address recorded for this order.</p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-blue-600">No customer record found.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">Order Items</h3>

                                {canUpdateOrders && viewingOrder?.order_status === 'preparing' && (
                                    <button
                                        onClick={() => handleBulkMarkAllReady(viewingOrder.order_id)}
                                        className="text-xs px-3 py-1.5 bg-orange-600 text-white rounded-md font-semibold hover:bg-orange-700"
                                    >
                                        Mark All Ready
                                    </button>
                                )}
                            </div>

                            {(() => {
                                const activeItems = orderItems.filter(
                                    item => !['voided', 'void_approved', 'void_requested'].includes(item.line_status)
                                )
                                const voidedItems = orderItems.filter(
                                    item => ['voided', 'void_approved', 'void_requested'].includes(item.line_status)
                                )

                                const renderItemRow = (item, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-3 text-sm text-gray-800">
                                            <div className="flex items-center gap-3">
                                                {item.menu_items?.item_img && (
                                                    <img
                                                        src={item.menu_items.item_img}
                                                        alt={item.menu_items.name}
                                                        className="w-10 h-10 rounded-lg object-cover"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium">{item.menu_items?.name || 'Unknown Item'}</p>
                                                    {item.menu_items?.category && (
                                                        <p className="text-xs text-gray-500">
                                                            {item.menu_items.category}
                                                            {item.menu_items?.classification && (
                                                                <span className="ml-1 text-gray-400">· {item.menu_items.classification}</span>
                                                            )}
                                                        </p>
                                                    )}
                                                    {item.order_item_variants?.length > 0 && (
                                                        <div className="mt-1 space-y-1">
                                                            {item.order_item_variants.map((variantRow, idx) => (
                                                                <p key={`variant-${idx}`} className="text-xs text-gray-500">
                                                                    <span className="font-medium text-gray-600">
                                                                        {variantRow.item_variants?.category || 'Variant'}:
                                                                    </span>{' '}
                                                                    {variantRow.item_variants?.variant_name || 'Unnamed Variant'}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {item.order_item_modifiers?.length > 0 && (
                                                        <div className="mt-1 space-y-1">
                                                            {item.order_item_modifiers.map((modifierRow, idx) => (
                                                                <p key={`modifier-${idx}`} className="text-xs text-[#A0826D]">
                                                                    + {modifierRow.item_modifiers?.modifier_name || 'Unnamed Add-on'}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {item.special_instructions && (
                                                        <p className="mt-1 text-xs text-gray-500 italic">
                                                            Note: {item.special_instructions}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                                        <td className="px-4 py-3 text-sm text-center">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                                item.prep_status === 'completed'
                                                    ? 'bg-gray-100 text-gray-600'
                                                    : item.prep_status === 'ready'
                                                        ? 'bg-green-100 text-green-800'
                                                        : item.prep_status === 'preparing'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {item.prep_status === 'completed'
                                                    ? 'Completed'
                                                    : item.prep_status === 'ready'
                                                        ? 'Ready'
                                                        : item.prep_status === 'preparing'
                                                            ? 'Preparing'
                                                            : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(item.item_price)}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-800 text-right">
                                            {formatCurrency(parseFloat(item.item_price || 0) * parseFloat(item.quantity || 0))}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {canUpdateOrders &&
                                            viewingOrder?.order_status === 'preparing' &&
                                            item.line_status !== 'voided' &&
                                            item.line_status !== 'void_approved' &&
                                            item.prep_status !== 'ready' ? (
                                                <button
                                                    onClick={() => handleMarkItemReady(item.order_item_id, viewingOrder.order_id)}
                                                    className="px-3 py-1.5 bg-[#A0826D] text-white rounded-md text-xs font-medium hover:bg-[#8c705b]"
                                                >
                                                    Mark Ready
                                                </button>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                )

                                const tableHeader = (
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Qty</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Prep Status</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Action</th>
                                        </tr>
                                    </thead>
                                )

                                return (
                                    <div className="space-y-4">
                                        {/* Active items table */}
                                        {activeItems.length > 0 ? (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <table className="w-full">
                                                    {tableHeader}
                                                    <tbody className="divide-y divide-gray-200">
                                                        {activeItems.map((item, index) => renderItemRow(item, index))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-sm">No active items found for this order.</p>
                                        )}

                                        {/* Voided items collapsible */}
                                        {voidedItems.length > 0 && (
                                            <div className="border border-red-100 rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => setShowVoidedItems(v => !v)}
                                                    className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
                                                            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                                                        </svg>
                                                        <span className="text-sm font-semibold text-red-700">
                                                            Voided Items
                                                        </span>
                                                        <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
                                                            {voidedItems.length}
                                                        </span>
                                                    </div>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16" height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        className={`text-red-400 transition-transform duration-200 ${showVoidedItems ? 'rotate-180' : ''}`}
                                                    >
                                                        <path d="M6 9l6 6 6-6"/>
                                                    </svg>
                                                </button>

                                                {showVoidedItems && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-red-50/60">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-red-400">Item</th>
                                                                    <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Qty</th>
                                                                    <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Void Reason</th>
                                                                    <th className="px-4 py-2 text-right text-xs font-medium text-red-400">Price</th>
                                                                    <th className="px-4 py-2 text-right text-xs font-medium text-red-400">Total</th>
                                                                    <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-red-50">
                                                                {voidedItems.map((item, index) => (
                                                                    <tr key={index} className="opacity-60">
                                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                                            <div className="flex items-center gap-3">
                                                                                {item.menu_items?.item_img && (
                                                                                    <img
                                                                                        src={item.menu_items.item_img}
                                                                                        alt={item.menu_items.name}
                                                                                        className="w-10 h-10 rounded-lg object-cover grayscale"
                                                                                    />
                                                                                )}
                                                                                <div>
                                                                                    <p className="font-medium line-through text-gray-400">
                                                                                        {item.menu_items?.name || 'Unknown Item'}
                                                                                    </p>
                                                                                    {item.order_item_variants?.length > 0 && (
                                                                                        <div className="mt-1 space-y-1">
                                                                                            {item.order_item_variants.map((variantRow, idx) => (
                                                                                                <p key={idx} className="text-xs text-gray-400">
                                                                                                    {variantRow.item_variants?.variant_name}
                                                                                                </p>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}
                                                                                    {item.order_item_modifiers?.length > 0 && (
                                                                                        <div className="mt-1 space-y-1">
                                                                                            {item.order_item_modifiers.map((modifierRow, idx) => (
                                                                                                <p key={idx} className="text-xs text-gray-400">
                                                                                                    + {modifierRow.item_modifiers?.modifier_name}
                                                                                                </p>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-400 text-center">{item.quantity}</td>
                                                                        <td className="px-4 py-3 text-center">
                                                                            <span className="text-xs text-gray-400 italic">
                                                                                {item.void_reason
                                                                                    ? item.void_reason.replace(/_/g, ' ')
                                                                                    : '—'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-400 text-right line-through">
                                                                            {formatCurrency(item.item_price)}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-400 text-right line-through">
                                                                            {formatCurrency(parseFloat(item.item_price || 0) * parseFloat(item.quantity || 0))}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-center">
                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                                                                {item.line_status === 'void_approved' ? 'Void Approved' : 'Voided'}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })()}
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Summary</h3>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-medium">{formatCurrency(viewingOrder.subtotal)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">VAT (12%) Included Tax</span>
                                    <span className="font-medium">{formatCurrency(viewingOrder.tax)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Discount</span>
                                    <span className="font-medium text-green-600">-{formatCurrency(viewingOrder.discount)}</span>
                                </div>
                                <div className="border-t border-gray-300 pt-2 flex justify-between">
                                    <span className="text-lg font-bold">Total</span>
                                    <span className="text-lg font-bold text-[#A0826D]">
                                        {formatCurrency(viewingOrder.final_amount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
                        {viewingOrder.order_status === 'completed' && canUpdateOrders && (
                            <button
                                onClick={() => { setShowViewModal(false); handleOpenRemakeModal(viewingOrder) }}
                                disabled={remakeRequestOrderIds.has(viewingOrder.order_id)}
                                className={`px-5 py-2 rounded-lg font-medium text-sm border ${
                                    remakeRequestOrderIds.has(viewingOrder.order_id)
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100'
                                }`}
                            >
                                {remakeRequestOrderIds.has(viewingOrder.order_id) ? 'Remake Requested' : 'Request Remake'}
                            </button>
                        )}
                        {['pending', 'preparing'].includes(viewingOrder.order_status) && (
                            <button
                                onClick={() => {
                                    setPendingCancelOrderId(viewingOrder.order_id)
                                    setCancelPinInput('')
                                    setShowCancelPinModal(true)
                                }}
                                className="px-5 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg font-medium hover:bg-red-100 text-sm"
                            >
                                Cancel Order
                            </button>
                        )}

                        <div className="flex gap-3 ml-auto">
                            <button
                                onClick={() => {
                                    setShowViewModal(false)
                                    setViewingOrder(null)
                                    setOrderItems([])
                                    setShowVoidedItems(false)
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>

                            {canUpdateOrders && getNextStatus(viewingOrder.order_status) && (
                                <button
                                    onClick={() => {
                                        handleStatusChange(viewingOrder.order_id, getNextStatus(viewingOrder.order_status))
                                        setShowViewModal(false)
                                        setViewingOrder(null)
                                        setOrderItems([])
                                    }}
                                    className="px-6 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8c705b]"
                                >
                                    Move to {getNextStatus(viewingOrder.order_status)?.charAt(0).toUpperCase() + getNextStatus(viewingOrder.order_status)?.slice(1)}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}

        {showRemakeModal && remakeOrder && (
            <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40" onClick={() => setShowRemakeModal(false)} />
                <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <h2 className="text-lg font-bold text-gray-800">Request Remake</h2>
                        <p className="text-sm text-gray-600 mt-1">Order #{remakeOrder.reference_number || remakeOrder.order_id}</p>
                    </div>
                    <div className="px-6 py-4 space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-semibold text-gray-600">Which item(s) need to be remade?</p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (selectedRemakeItemIds.size === remakeOrderItems.length) {
                                            setSelectedRemakeItemIds(new Set())
                                        } else {
                                            setSelectedRemakeItemIds(new Set(remakeOrderItems.map(i => i.order_item_id)))
                                        }
                                    }}
                                    className="text-xs text-[#A0826D] hover:underline"
                                >
                                    {selectedRemakeItemIds.size === remakeOrderItems.length ? 'Deselect all' : 'Select all'}
                                </button>
                            </div>
                            <div className="border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                                {remakeOrderItems.length === 0 ? (
                                    <p className="text-xs text-gray-400 px-3 py-3">No active items found for this order.</p>
                                ) : (
                                    remakeOrderItems.map((it, idx) => {
                                        const isChecked = selectedRemakeItemIds.has(it.order_item_id)
                                        return (
                                            <label key={idx} className={`px-3 py-2 text-sm flex items-center gap-2.5 cursor-pointer ${isChecked ? 'bg-orange-50' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => handleToggleRemakeItem(it.order_item_id)}
                                                    className="accent-orange-600"
                                                />
                                                <span className={isChecked ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                                                    {it.menu_items?.name || 'Item'} × {it.quantity}
                                                </span>
                                            </label>
                                        )
                                    })
                                )}
                            </div>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {selectedRemakeItemIds.size} of {remakeOrderItems.length} item(s) selected — only these will be remade in POS.
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Who is responsible for the remake?</p>
                            <div className="space-y-1.5">
                                {remakeStaffOptions.length === 0 && (
                                    <p className="text-xs text-gray-400">No staff found for this order.</p>
                                )}
                                {remakeStaffOptions.map(opt => (
                                    <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-700">
                                        <input type="checkbox" checked={remakeResponsibleStaff.has(opt.key)}
                                            onChange={() => handleToggleRemakeStaff(opt.key)} className="accent-[#A0826D]" />
                                        {opt.name} — {opt.role}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Reason for Remake</label>
                            <select value={remakeReason} onChange={e => setRemakeReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2">
                                <option value="">Select a reason...</option>
                                {REMAKE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {remakeReason === 'Other' && (
                                <textarea value={remakeOtherReason} onChange={e => setRemakeOtherReason(e.target.value)}
                                    placeholder="Describe the reason..." rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                            )}
                        </div>
                    </div>
                    <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                        <button onClick={() => setShowRemakeModal(false)}
                            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                        <button onClick={handleSubmitRemakeRequest} disabled={submittingRemake}
                            className="px-5 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50">
                            {submittingRemake ? 'Submitting...' : 'Submit Remake Request'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {showCancelPinModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
                    <h2 className="text-lg font-bold mb-1 text-gray-800">Admin Authorization</h2>
                    <p className="text-sm text-gray-500 mb-4">Enter Admin PIN to cancel this order.</p>
                    <input
                        type="password"
                        inputMode="numeric"
                        value={cancelPinInput}
                        onChange={(e) => setCancelPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        placeholder="Enter Admin PIN"
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => { setShowCancelPinModal(false); setCancelPinInput(''); setPendingCancelOrderId(null) }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >Cancel</button>
                        <button
                            onClick={verifyCancelPin}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >Confirm</button>
                    </div>
                </div>
            </div>
        )}
    </div>
    )
}
